// src/components/employee/ChangePassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Container
} from '@mui/material';
import {
  Lock as LockIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useEmployeeAuth } from '../../hooks/useEmployeeAuth';

export default function ChangePassword({ employee: propEmployee }) {
  const navigate = useNavigate();
  const { employee: authEmployee, changePassword, loading } = useEmployeeAuth();
  
  // Use employee from props (passed by ProtectedEmployeeRoute) or fallback to auth hook
  const employee = propEmployee || authEmployee;
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])/.test(password)) {
      return 'Password must contain at least one uppercase and one lowercase letter';
    }
    if (!/(?=.*\d)/.test(password)) {
      return 'Password must contain at least one number';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.newPassword || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword === 'Welcome1') {
      setError('Please choose a different password than the default');
      return;
    }

    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      await changePassword(formData.newPassword);
      setSuccess(true);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/employee/dashboard');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    }
  };

  // Show loading if employee data is not available
  if (!employee) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Loading your account...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        display: 'flex',
        alignItems: 'center',
        py: 3
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={4} alignItems="center">
          {/* Header */}
          <Box sx={{ textAlign: 'center' }}>
            <SecurityIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
              Set New Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome, {employee.firstName}! Please change your password to continue.
            </Typography>
          </Box>

          {/* Password Change Card */}
          <Card sx={{ width: '100%', maxWidth: 400 }}>
            <CardContent sx={{ p: 4 }}>
              {success ? (
                <Box sx={{ textAlign: 'center' }}>
                  <Alert severity="success" sx={{ mb: 3 }}>
                    Password changed successfully! Redirecting to your dashboard...
                  </Alert>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
                    Create Secure Password
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <TextField
                        name="newPassword"
                        type="password"
                        label="New Password"
                        value={formData.newPassword}
                        onChange={handleChange}
                        fullWidth
                        disabled={loading}
                        InputProps={{
                          startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />

                      <TextField
                        name="confirmPassword"
                        type="password"
                        label="Confirm New Password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        fullWidth
                        disabled={loading}
                        InputProps={{
                          startAdornment: <LockIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={loading}
                        sx={{ py: 1.5 }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          'Change Password'
                        )}
                      </Button>
                    </Stack>
                  </form>

                  {/* Password Requirements */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      Password Requirements:
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      • At least 6 characters long
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      • At least one uppercase letter
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      • At least one lowercase letter
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      • At least one number
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
}
